import { useQuery } from "@tanstack/react-query";
import useApi from "./useGaneshApi";

export type FulfillmentsAnalyticsType =
  | "health"
  | "export_ftq"
  | "export_stats"
  | "connector_usage_stats"
  | "connector_ftq";

interface UseFulfillmentsAnalyticsParams {
  dateStart?: string;
  dateEnd?: string;
  type: FulfillmentsAnalyticsType;
  enabled?: boolean;
}

/**
 * Centralized hook for fetching fulfillments analytics data.
 * Provides consistent caching, error handling, and query key management.
 *
 * Cache Strategy:
 * - Health data: 2 hours (comprehensive, rarely changes)
 * - Individual metrics: 1 hour (more granular, changes more frequently)
 */
export const useFulfillmentsAnalytics = ({
  dateStart,
  dateEnd,
  type,
  enabled = true,
}: UseFulfillmentsAnalyticsParams) => {
  const { apiRequest } = useApi();

  const fetchData = async () => {
    const queryParams = new URLSearchParams();
    if (dateStart) queryParams.append("dateStart", dateStart);
    if (dateEnd) queryParams.append("dateEnd", dateEnd);
    queryParams.append("type", type);

    const url = `/v1/fulfillments_analytics/statistics?${queryParams.toString()}`;

    const response = await apiRequest<any[]>(url, {
      method: "GET",
    });
    return response.data || [];
  };

  // Determine cache duration based on data type
  const getCacheDuration = () => {
    switch (type) {
      case "health":
        return 2 * 60 * 60 * 1000; // 2 hours - comprehensive data
      default:
        return 60 * 60 * 1000; // 1 hour - individual metrics
    }
  };

  return useQuery({
    queryKey: ["fulfillmentsAnalytics", type, dateStart, dateEnd],
    queryFn: fetchData,
    enabled: enabled && !!(dateStart && dateEnd),
    staleTime: getCacheDuration(),
    refetchInterval: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook for fetching comprehensive health metrics (parent query).
 * This is the most commonly used query that combines all other metrics.
 */
export const useHealthMetrics = (dateStart?: string, dateEnd?: string) => {
  return useFulfillmentsAnalytics({
    dateStart,
    dateEnd,
    type: "health",
  });
};

/**
 * Hook for fetching individual metric types.
 * Use these when you only need specific data subsets.
 */
export const useExportFTQ = (dateStart?: string, dateEnd?: string) => {
  return useFulfillmentsAnalytics({
    dateStart,
    dateEnd,
    type: "export_ftq",
  });
};

export const useExportStats = (dateStart?: string, dateEnd?: string) => {
  return useFulfillmentsAnalytics({
    dateStart,
    dateEnd,
    type: "export_stats",
  });
};

export const useConnectorUsageStats = (
  dateStart?: string,
  dateEnd?: string
) => {
  return useFulfillmentsAnalytics({
    dateStart,
    dateEnd,
    type: "connector_usage_stats",
  });
};

export const useConnectorFTQ = (dateStart?: string, dateEnd?: string) => {
  return useFulfillmentsAnalytics({
    dateStart,
    dateEnd,
    type: "connector_ftq",
  });
};
